import type { PassportSchemaDef } from "../types.js";

/**
 * EARLY-POSITIONING DRAFT — "energy-related products" (ErP) is the broad
 * Ecodesign/Energy-labelling family (washing machines, dishwashers, fridges,
 * ovens, HVAC, lighting, motors…) that ESPR absorbs. Derived from the
 * electrical/energy reference (electronics-v1) with appliance-specific accents:
 * the EU Energy Label class + annual consumption, water use per cycle, noise
 * class, and refrigerant type/GWP (F-gas relevant for cooling appliances).
 *
 * High Turkish relevance — the country is Europe's #1 white-goods producer
 * (Arçelik/Beko, Vestel). Field ids are STABLE; trued up when the delegated
 * acts consolidate under ESPR by adding fields / flipping `required`/`tier`.
 */
export const energyRelatedV1: PassportSchemaDef = {
  id: "energy-related-v1",
  version: "1.0.0-draft",
  title: {
    tr: "Enerji İlişkili Ürün Pasaportu",
    en: "Energy-related Product Passport",
    de: "Produktpass für energieverbrauchsrelevante Produkte",
  },
  jsonLdContext: {
    "@version": 1.1,
    "@vocab": "https://vocab.idpp.net/energy-related/v1#",
    schema: "https://schema.org/",
    gs1: "https://gs1.org/voc/",
    identifier: "gs1:gtin",
    issuer: "schema:issuedBy",
    issuedAt: "schema:dateCreated",
  },
  categories: [
    {
      id: "identity",
      labels: { tr: "Ürün Kimliği", en: "Product Identity", de: "Produktidentität" },
      fields: [
        {
          id: "identity.productName",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Ürün adı", en: "Product name", de: "Produktname" },
        },
        {
          id: "identity.productDescription",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Ürün özeti", en: "Product summary", de: "Produktbeschreibung" },
          description: "Short description of the product and its intended use.",
          presentation: { kind: "summary" },
        },
        {
          id: "identity.gtin",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "GTIN", en: "GTIN", de: "GTIN" },
          description: "GS1 Global Trade Item Number (8/12/13/14 digits).",
        },
        {
          id: "identity.productCategory",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: [
            "washing-machine",
            "dishwasher",
            "refrigerator",
            "freezer",
            "oven",
            "tumble-dryer",
            "water-heater",
            "air-conditioner",
            "heat-pump",
            "lighting",
            "electric-motor",
            "pump",
            "other",
          ],
          labels: { tr: "Ürün kategorisi", en: "Product category", de: "Produktkategorie" },
        },
        {
          id: "identity.brand",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Marka", en: "Brand", de: "Marke" },
        },
        {
          id: "identity.model",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Model", en: "Model", de: "Modell" },
        },
        {
          id: "identity.manufacturerName",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Üretici adı", en: "Manufacturer name", de: "Herstellername" },
        },
        {
          id: "identity.countryOfManufacture",
          type: "country",
          required: true,
          tier: "public",
          labels: { tr: "Üretim ülkesi", en: "Country of manufacture", de: "Herstellungsland" },
          description: "ISO 3166-1 alpha-2 code (e.g. TR).",
        },
        {
          id: "identity.serialOrBatch",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "Seri/parti numarası", en: "Serial/batch number", de: "Serien-/Chargennummer" },
        },
        {
          id: "identity.commodityCode",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "Emtia kodu (GTİP/CN)", en: "Commodity code (CN/HS)", de: "Warennummer (KN/HS)" },
          description: "Needed for the EU Registry registration record.",
        },
      ],
    },
    {
      id: "energy",
      labels: { tr: "Enerji ve Kaynak", en: "Energy & Resources", de: "Energie & Ressourcen" },
      fields: [
        {
          id: "energy.energyEfficiencyClass",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: ["a", "b", "c", "d", "e", "f", "g", "not-rated"],
          labels: {
            tr: "Enerji verimlilik sınıfı",
            en: "Energy efficiency class",
            de: "Energieeffizienzklasse",
          },
          highlight: true,
        },
        {
          id: "energy.energyConsumptionAnnualKwh",
          type: "number",
          required: false,
          tier: "public",
          unit: "kWh/year",
          labels: {
            tr: "Yıllık enerji tüketimi",
            en: "Annual energy consumption",
            de: "Jährlicher Energieverbrauch",
          },
          highlight: true,
        },
        {
          id: "energy.energyLabelUrl",
          type: "url",
          required: false,
          tier: "public",
          labels: { tr: "Enerji etiketi bağlantısı", en: "Energy label link", de: "Energielabel-Link" },
        },
        {
          id: "energy.ecodesignRegulation",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Ekotasarım regülasyonu", en: "Ecodesign regulation", de: "Ökodesign-Verordnung" },
        },
        {
          id: "energy.waterConsumptionLPerCycle",
          type: "number",
          required: false,
          tier: "public",
          unit: "L/cycle",
          labels: {
            tr: "Çevrim başına su tüketimi",
            en: "Water consumption per cycle",
            de: "Wasserverbrauch pro Zyklus",
          },
          highlight: true,
        },
        {
          id: "energy.noiseLevelDb",
          type: "number",
          required: false,
          tier: "public",
          unit: "dB",
          labels: { tr: "Gürültü seviyesi", en: "Noise level", de: "Geräuschpegel" },
          highlight: true,
        },
        {
          id: "energy.noiseClass",
          type: "enum",
          required: false,
          tier: "public",
          enumValues: ["a", "b", "c", "d"],
          labels: { tr: "Gürültü sınıfı", en: "Noise class", de: "Geräuschklasse" },
        },
        {
          id: "energy.capacity",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Kapasite", en: "Capacity", de: "Kapazität" },
          description: "E.g. kg load, litres, place settings.",
        },
      ],
    },
    {
      id: "materials",
      labels: { tr: "Malzemeler ve Maddeler", en: "Materials & Substances", de: "Materialien & Stoffe" },
      fields: [
        {
          id: "materials.rohsCompliant",
          type: "boolean",
          required: true,
          tier: "public",
          labels: { tr: "RoHS uyumlu", en: "RoHS compliant", de: "RoHS-konform" },
        },
        {
          id: "materials.svhcPresent",
          type: "boolean",
          required: false,
          tier: "public",
          labels: {
            tr: "SVHC içerir (>%0,1 a/a)",
            en: "Contains SVHC (>0.1% w/w)",
            de: "Enthält SVHC (>0,1 % w/w)",
          },
        },
        {
          id: "materials.recycledContentPct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: { tr: "Geri dönüştürülmüş içerik", en: "Recycled content", de: "Rezyklatanteil" },
        },
        {
          id: "materials.refrigerantType",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Soğutucu akışkan türü", en: "Refrigerant type", de: "Kältemitteltyp" },
          description: "For cooling appliances, e.g. R600a.",
        },
        {
          id: "materials.refrigerantGwp",
          type: "number",
          required: false,
          tier: "public",
          labels: { tr: "Soğutucu GWP değeri", en: "Refrigerant GWP", de: "Kältemittel-GWP" },
          description: "Global warming potential of the refrigerant (F-gas relevant).",
        },
      ],
    },
    {
      id: "repairability",
      labels: { tr: "Onarılabilirlik", en: "Repairability", de: "Reparierbarkeit" },
      fields: [
        {
          id: "repairability.repairabilityScore",
          type: "enum",
          required: false,
          tier: "public",
          enumValues: ["A", "B", "C", "D", "E"],
          labels: { tr: "Onarılabilirlik sınıfı", en: "Repairability score", de: "Reparierbarkeitsklasse" },
          highlight: true,
        },
        {
          id: "repairability.sparePartsAvailable",
          type: "boolean",
          required: true,
          tier: "public",
          labels: { tr: "Yedek parça mevcut", en: "Spare parts available", de: "Ersatzteile verfügbar" },
        },
        {
          id: "repairability.sparePartsYears",
          type: "number",
          required: false,
          tier: "public",
          unit: "years",
          labels: {
            tr: "Yedek parça bulunabilirlik süresi",
            en: "Spare parts availability period",
            de: "Ersatzteil-Verfügbarkeitsdauer",
          },
          highlight: true,
        },
        {
          id: "repairability.repairInstructionsUrl",
          type: "url",
          required: false,
          tier: "public",
          labels: { tr: "Onarım talimatları", en: "Repair instructions", de: "Reparaturanleitung" },
        },
        {
          id: "repairability.disassemblyInfo",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "Söküm bilgisi", en: "Disassembly information", de: "Demontageinformationen" },
        },
        {
          id: "repairability.warrantyYears",
          type: "number",
          required: false,
          tier: "public",
          unit: "years",
          labels: { tr: "Garanti süresi", en: "Warranty period", de: "Garantiedauer" },
        },
      ],
    },
    {
      id: "circularity",
      labels: { tr: "Döngüsellik", en: "Circularity", de: "Kreislauffähigkeit" },
      fields: [
        {
          id: "circularity.recyclable",
          type: "boolean",
          required: true,
          tier: "public",
          labels: { tr: "Geri dönüştürülebilir", en: "Recyclable", de: "Recycelbar" },
        },
        {
          id: "circularity.recyclabilityPct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: {
            tr: "Geri dönüştürülebilirlik oranı",
            en: "Recyclability rate",
            de: "Recyclingfähigkeitsquote",
          },
        },
        {
          id: "circularity.weeeCategory",
          type: "enum",
          required: false,
          tier: "public",
          enumValues: [
            "1-temperature-exchange",
            "2-screens",
            "3-lamps",
            "4-large-equipment",
            "5-small-equipment",
            "6-small-it",
            "other",
          ],
          labels: { tr: "WEEE kategorisi", en: "WEEE category", de: "WEEE-Kategorie" },
        },
        {
          id: "circularity.collectionInfo",
          type: "string",
          required: true,
          tier: "public",
          labels: {
            tr: "Ömür sonu toplama bilgisi",
            en: "End-of-life collection information",
            de: "Informationen zur Altgerätesammlung",
          },
          description: "How to return the product for WEEE collection/recycling.",
        },
        {
          id: "circularity.takeBackProgramUrl",
          type: "url",
          required: false,
          tier: "public",
          labels: { tr: "Geri alma programı", en: "Take-back program", de: "Rücknahmeprogramm" },
        },
        {
          id: "circularity.hazardousComponentsRemoval",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Tehlikeli bileşen çıkarma bilgisi",
            en: "Hazardous component removal",
            de: "Entfernung gefährlicher Komponenten",
          },
          description: "For recyclers: refrigerant recovery, compressor oil, capacitors.",
        },
      ],
    },
  ],
};
